
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

import { getLocalities } from "@/services/property";
import { localities as localityData } from "@/services/localityData";
import { Loader2 } from "lucide-react";
import { capitalizeWords } from "@/helper";
import SEO from "@/components/seo/seo";

const Locations = () => {
  const [locations, setLocations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await getLocalities();

        // Map API data with static data for images and slugs
        const mappedLocations = data.localities.map((loc: any) => {
          const staticData = localityData.find(
            (l) => l.name.toLowerCase() === loc.locality.toLowerCase() || l.slug.toLowerCase() === loc.locality.toLowerCase()
          );

          return {
            name: loc.locality,
            count: loc.count,
            // Use static data if available, otherwise fallback
            slug: staticData?.slug || loc.locality.toLowerCase().replace(/\s+/g, '-'),
            image: staticData?.projectImage || '/placeholder.svg',
            tags: [] // We don't have tags in the API or static data currently, could add to static data later
          };
        });

        setLocations(mappedLocations);
      } catch (error) {
        console.error("Error fetching localities:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, []);

  return (
    <>
      <SEO
        title="Explore Dubai Locations | Urban Oasis UAE"
        description="Discover the most desirable neighborhoods and communities across Dubai, from waterfront living to urban hubs and family-friendly communities."
        url="/locations"
        image="/image/logo.png"
        keywords="dubai real estate, urban oasis"
      />
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Explore Dubai Locations</h1>
            <p className="text-muted-foreground max-w-2xl">
              Discover the most desirable neighborhoods and communities across Dubai, from
              waterfront living to urban hubs and family-friendly communities.
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Card key={i} className="h-full overflow-hidden">
                  <Skeleton className="aspect-square w-full" />
                  <CardHeader className="pb-2">
                    <Skeleton className="h-6 w-3/4" />
                  </CardHeader>
                  <CardContent className="pb-2">
                    <Skeleton className="h-4 w-1/2" />
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-4 w-32" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {locations.map((location, index) => (
                <Link to={`/locations/${location.slug}`} key={index}>
                  <Card className="h-full overflow-hidden transition-all hover:shadow-lg group">
                    <div className="aspect-square w-full overflow-hidden">
                      <img
                        src={location.image}
                        alt={capitalizeWords(location.name)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <CardHeader className="pb-2">
                      <CardTitle className="line-clamp-1">{capitalizeWords(location.name)}</CardTitle>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <p className="text-sm text-muted-foreground">{location.count} Properties</p>
                    </CardContent>
                    <CardFooter>
                      <span className="text-sm font-medium text-primary group-hover:text-gold transition-colors">View Details →</span>
                    </CardFooter>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Locations;
